/**
 * Stone Investment Team Section
 * 
 * Team member showcase with Stone Investment's luxury aesthetic:
 * - Editorial grid layout
 * - Elegant hover effects
 * - Social links
 */

'use client';

import Image from 'next/image';
import { useState } from 'react';
import type { TeamSectionProps } from '@routeroyal/theme-core';

/**
 * Individual team member card
 */
function TeamMemberCard({ 
  member, 
}: { 
  member: TeamSectionProps['members'][0]; 
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      className="group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Photo */}
      {member.image && (
        <div className="relative aspect-[3/4] overflow-hidden bg-stone-200 mb-6">
          <Image
            src={member.image}
            alt={member.name}
            fill
            className={`object-cover transition-transform duration-[2000ms] ease-out ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
          
          {/* Overlay on hover */}
          <div className={`absolute inset-0 bg-stone-900/20 transition-opacity duration-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`} />
        </div>
      )}
      
      {/* Info */}
      <div className="border-t border-stone-200 pt-4">
        <h3 
          className={`font-serif text-2xl lg:text-3xl text-stone-900 mb-1 transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {member.name}
        </h3>
        <p 
          className="text-sm font-sans text-stone-600 mb-4 uppercase tracking-[0.1em]"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          {member.role}
        </p>
        
        {member.bio && (
          <p 
            className="text-sm text-stone-600 leading-relaxed font-sans mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {member.bio}
          </p>
        )}
        
        {/* Social Links */}
        {member.social && (
          <div className="flex gap-4">
            {member.social.linkedin && (
              <a
                href={member.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-400 hover:text-stone-700 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            )}
            {member.social.twitter && (
              <a
                href={member.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-400 hover:text-stone-700 transition-colors"
                aria-label="Twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            )}
            {member.social.email && (
              <a
                href={`mailto:${member.social.email}`}
                className="text-stone-400 hover:text-stone-700 transition-colors"
                aria-label="Email"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StoneTeamSection - Team member showcase
 */
export function StoneTeamSection({
  title,
  subtitle,
  members,
  layout = 'grid'
}: TeamSectionProps) {
  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
            Our Team
          </p>
          <h2 
            className="font-serif text-4xl lg:text-5xl text-stone-900 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h2>
          
          {subtitle && (
            <p 
              className="text-lg text-stone-600 font-sans"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {subtitle}
            </p>
          )}
        </div>
        
        {/* Team Grid */}
        {layout === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
            {members.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </div>
        )}
        
        {/* Featured Layout */}
        {layout === 'featured' && members.length > 0 && (
          <div className="space-y-20">
            {/* Featured Member */}
            {members[0] && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                {members[0].image && (
                  <div className="relative aspect-[4/5] overflow-hidden bg-stone-200">
                    <Image
                      src={members[0].image}
                      alt={members[0].name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                    />
                  </div>
                )}
                
                <div>
                  <h3 
                    className="font-serif text-4xl lg:text-5xl text-stone-900 mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {members[0].name}
                  </h3>
                  <p className="text-lg text-stone-600 mb-8 font-sans uppercase tracking-[0.1em]">
                    {members[0].role}
                  </p>
                  {members[0].bio && (
                    <p className="text-lg text-stone-600 leading-relaxed font-sans">
                      {members[0].bio}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Other Members */}
            {members.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                {members.slice(1).map((member, index) => (
                  <TeamMemberCard key={index} member={member} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default StoneTeamSection;
